<template>
    <div class="password-security">
        <el-alert
            v-if="error"
            :class="darkMode ? 'dark-alert' : ''"
            title=""
            type="error"
            style="margin-bottom: 18px;"
        >
            <span v-if="error">{{ error }}</span>
        </el-alert>
        <security-rules
            :dark-mode="darkMode"
            :current-lang="currentLang"
            :server-error="serverError"
            :server-error-message="serverErrorMessage"
        ></security-rules>
        <el-form
            ref="ChangePasswordForm"
            :rules="passwordValidationRules"
            :model="changePasswordData"
            label-position="top"
            status-icon
            novalidate
            label-width="120px"
            class="demo-ruleForm"
            @submit="submitChangePasswordForm"
            @keyup.enter.native="submitChangePasswordForm"
        >
            <el-form-item
                prop="password1"
            >
                <label class="message">
                    {{ getVerbose('user_prompt_change_password_message') }}
                    <i class="ti-eye" :class="passwordFieldType" @click="switchPasswordInputType"></i>
                </label>
                <el-input
                    v-model="changePasswordData.password1"
                    :type="passwordFieldType"
                    autocomplete="new-password"
                ></el-input>
            </el-form-item>
            <el-form-item
                v-if="confirmPassword"
                prop="password2"
            >
                <label class="message">
                    {{ getVerbose('user_prompt_change_password_message_check') }}
                </label>
                <el-input
                    v-model="changePasswordData.password2"
                    :type="passwordFieldType"
                    class="password-check"
                    autocomplete="new-password"
                ></el-input>
            </el-form-item>
            <el-form-item
                class="submit-button-container"
                :class="alignButton"
            >
                <!-- alignButton -->
                <el-button
                    type="submit"
                    class="el-button el-button--primary"
                    :class="alignButton"
                    v-if="!hideButton"
                    @click="submitChangePasswordForm"
                >
                    <i
                        v-if="connecting"
                        class="el-icon-loading"
                    ></i>
                    {{ buttonText }}
                </el-button>
            </el-form-item>
        </el-form>

    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import HttpErrorManager from '../partials/http-error-manager.js';
import SecurityRules from './rules-alert.vue';
export default {

    components: {
        SecurityRules
    },

    props: {
        email: {
            type: String,
            required: true,
        },
        passwordChangeToken: {
            type: String,
            required: false,
            default(){
                return null;
            }
        },
        confirmPassword: {
            type: Boolean,
            default: true,
        },
        hideButton: {
            type: Boolean,
            default: false,
        },
        alignButton: {
            type: String,
            default(){
                return 'left';
            },
            validator: function (value) {
                return ['left', 'right', 'center'].includes(value);
            },
        },
        enableNotification: {
            type: Boolean,
            default: true,
        },
        buttonText: {
            type: String,
            default(){
                return 'Change password';
            }
        }
    },

    data (){
        return {
            changePasswordData:{
                password1: null,
                password2: null,
            },
            passwordFieldType: 'password',
            error: '',
            password: '',
            passwordCheck: '',
            serverError: '',
            serverErrorMessage: '',
            connecting: false,
        };
    },

    computed: {
        ...mapGetters('fundation', [
            'getVerbose',
            'currentLang',
            'darkMode',
        ]),

        passwordValidationRules(){
            let validatePass1 = (rule, value, callback) => {
                if (!value) {
                    callback(new Error(this.getVerbose('field_required')));
                }
                else{
                    callback();
                }
            }
            let validatePass2 = (rule, value, callback) => {
                if(this.confirmPassword === false){
                    callback();
                    return
                }
                if (!value) {
                    callback(new Error(this.getVerbose('field_required')));
                }
                else if (value !== this.changePasswordData.password1) {
                    callback(new Error(this.getVerbose('login_passwords_dont_match')));
                }
                else{
                    callback();
                }
            };
            return {
                password1: [
                    { validator: validatePass1, trigger: 'blur' }
                ],
                password2: [
                    { validator: validatePass2, trigger: 'blur' }
                ],
            }
        },

    },

    mounted (){
        this.httpErrorManager = new HttpErrorManager(this.currentLang);
    },

    methods: {
        switchPasswordInputType(){
            this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
        },
        submitChangePasswordForm(event) {
            let credentials = {
                email: this.email,
                password1: this.changePasswordData.password1,
                password2: this.changePasswordData.password2,
            }
            if(this.passwordChangeToken){
                credentials.password_change_token = this.passwordChangeToken;
            }
            if(event){
                event.preventDefault();
            }
            this.$refs.ChangePasswordForm.validate().then(() => {
                this.error = '';
                this.connecting = true;

                this.$store.dispatch(
                   'changeUserPassword',
                   credentials
                ).then((response) => {

                    this.$emit('success', {
                        response: response.data,
                        email: this.email,
                        password: this.changePasswordData.password1,
                    });
                    if(this.enableNotification){
                        this.$notify.success({
                            title: this.getVerbose('user_prompt_change_password_success_title'),
                            message: `${this.getVerbose('user_prompt_change_password_success_message')} ${this.email}`
                        });
                    }
                    this.changePasswordData.password1 = null;
                    this.changePasswordData.password2 = null;

                }).catch((error) => {
                    this.serverError = this.httpErrorManager.getFirstCode(error.response.data._errors);
                    this.serverErrorMessage = this.httpErrorManager.getFirstCode(error.response.data.message);
                    this.$emit('error', error.response);

                }).finally(() => {
                    this.connecting = false;
                });
            });
        },
    },
};
</script>

<style lang="scss">
.dark-alert{
    background-color: #444 !important;
    color: #bbb !important;
}
.ti-eye.text{
    position: relative;
}
.ti-eye.text:after{
    position: absolute;
    content: "/";
    color: black;
    font-size: 1.8em;
    left: 5px;
    top: -8px;
}
.submit-button-container.left, .submit-button-container.right, .submit-button-container.center{
    // width: 100%;
    display: flex;
}
.submit-button-container.left{
    justify-content: start;
}
.submit-button-container.center{
    justify-content: flex-end;
}
.submit-button-container.right{
    justify-content: flex-end;
}

.password-security{
    .el-alert__content{
        width: 100%;
    }

    .rule-error{
        color: #F56C6C;
    }
    .rule-message{
        font-weight: bold;
    }
    .loading-rules{
        width: 100%;
        height: 120px;
    }
}
</style>



// WEBPACK FOOTER //
// node_modules/fundation-app-vuejs/src/components/auth/FormChangePassword.vue